import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Refresh as RefreshIcon, Search as SearchIcon } from '@mui/icons-material';
import {
  api,
  RegisteredEventMemberSummary,
  TrainingECBInviteSummary,
  UpcomingEventSummary,
} from '../../services/api';
import type { TrainingSessionSummary } from '../../services/api';
import EventAttendanceQrDialog from './EventAttendanceQrDialog';
import SessionsPanel from './SessionsPanel';
import SessionAttendeesDialog from './SessionAttendeesDialog';

interface TrainerManageEventsDialogProps {
  open: boolean;
  onClose: () => void;
}

function formatDateRange(start: string, end: string): string {
  const s = new Date(start);
  const e = new Date(end);
  const sValid = !Number.isNaN(s.getTime());
  const eValid = !Number.isNaN(e.getTime());
  if (!sValid && !eValid) return '-';
  const fmt = (d: Date) =>
    d.toLocaleString('en-SG', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  if (sValid && eValid) return `${fmt(s)} — ${fmt(e)}`;
  if (sValid) return fmt(s);
  return fmt(e);
}

function normalizeEventDescriptionHtml(html: string): string {
  return html.replace(
    /(src\s*=\s*["'])\/uploads\/event/gi,
    '$1https://portal.huttonsgroup.com/uploads/event',
  );
}

const TrainerManageEventsDialog: React.FC<TrainerManageEventsDialogProps> = ({
  open,
  onClose,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [events, setEvents] = useState<UpcomingEventSummary[]>([]);
  const [query, setQuery] = useState('');
  const [focusedEventId, setFocusedEventId] = useState<string | null>(null);
  const [detailTab, setDetailTab] = useState<'registered' | 'ecbs'>('registered');
  const [registeredMembers, setRegisteredMembers] = useState<RegisteredEventMemberSummary[]>([]);
  const [registeredMembersLoading, setRegisteredMembersLoading] = useState(false);
  const [registeredMembersError, setRegisteredMembersError] = useState<string | null>(null);
  const [ecbInvites, setEcbInvites] = useState<TrainingECBInviteSummary[]>([]);
  const [ecbInvitesLoading, setEcbInvitesLoading] = useState(false);
  const [ecbInvitesError, setEcbInvitesError] = useState<string | null>(null);
  const [sessionQrTarget, setSessionQrTarget] = useState<{
    session: TrainingSessionSummary;
    mode: 'clockIn' | 'clockOut';
    eventName: string;
  } | null>(null);
  const [sessionAttendeesTarget, setSessionAttendeesTarget] =
    useState<TrainingSessionSummary | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await api.getTrainerEvents();
      setEvents(
        list.map((e) => ({
          ...e,
          assignedCategories: (e.assignedCategories || []).map((c) => c.toUpperCase()),
        })),
      );
    } catch (e) {
      setEvents([]);
      setError(e instanceof Error ? e.message : 'Failed to load events');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) {
      setEvents([]);
      setQuery('');
      setFocusedEventId(null);
      setError(null);
      setRegisteredMembers([]);
      setEcbInvites([]);
      setSessionQrTarget(null);
      setSessionAttendeesTarget(null);
      return;
    }
    void load();
  }, [open, load]);

  const focusedEvent = useMemo(
    () => events.find((e) => e.id === focusedEventId) || null,
    [events, focusedEventId],
  );

  const skipsRegistration =
    focusedEvent?.registrationRequired === false || focusedEvent?.sourceType === 'TRANS';

  const loadMembers = useCallback(async (eventId: string) => {
    setRegisteredMembersLoading(true);
    setEcbInvitesLoading(true);
    setRegisteredMembersError(null);
    setEcbInvitesError(null);
    try {
      const [members, ecbs] = await Promise.all([
        api.getRegisteredEventMembers(eventId).catch((err) => {
          setRegisteredMembersError(
            err instanceof Error ? err.message : 'Failed to load registered users',
          );
          return [] as RegisteredEventMemberSummary[];
        }),
        api.getEventECBInvites(eventId).catch((err) => {
          setEcbInvitesError(err instanceof Error ? err.message : 'Failed to load ECBs');
          return [] as TrainingECBInviteSummary[];
        }),
      ]);
      setRegisteredMembers(members);
      setEcbInvites(ecbs);
    } finally {
      setRegisteredMembersLoading(false);
      setEcbInvitesLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!focusedEventId) {
      setRegisteredMembers([]);
      setEcbInvites([]);
      return;
    }
    setDetailTab('registered');
    void loadMembers(focusedEventId);
  }, [focusedEventId, loadMembers]);

  useEffect(() => {
    if (skipsRegistration && detailTab === 'registered') {
      setDetailTab('ecbs');
    }
  }, [skipsRegistration, detailTab]);

  const filteredEvents = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return events;
    return events.filter((e) => {
      const haystack = `${e.name} ${e.venue || ''} ${e.description || ''}`.toLowerCase();
      return haystack.includes(q);
    });
  }, [events, query]);

  const isRefreshing = registeredMembersLoading || ecbInvitesLoading;

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            height: { xs: '100%', md: 'min(88vh, 900px)' },
            maxHeight: { xs: '100%', md: 'min(88vh, 900px)' },
          },
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6" sx={{ flex: 1, fontWeight: 700 }}>
            Manage Events
          </Typography>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{ display: 'flex', flexDirection: 'column', gap: 2, minHeight: 0 }}
        >
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '320px 1px 1fr' },
              gap: 2,
              flex: 1,
              minHeight: 0,
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              <TextField
                size="small"
                placeholder="Search events"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 1 }}
              />
              <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                {loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress size={28} />
                  </Box>
                ) : filteredEvents.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                    No trainings assigned to you.
                  </Typography>
                ) : (
                  <List dense disablePadding>
                    {filteredEvents.map((event) => (
                      <ListItemButton
                        key={event.id}
                        selected={focusedEventId === event.id}
                        onClick={() => setFocusedEventId(event.id)}
                        sx={{ alignItems: 'flex-start', borderRadius: 1, mb: 0.5 }}
                      >
                        <ListItemText
                          primary={
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {event.name}
                            </Typography>
                          }
                          secondary={
                            <Stack spacing={0.25} component="span">
                              <Typography variant="caption" color="text.secondary" component="span">
                                {formatDateRange(event.startDate, event.endDate)}
                              </Typography>
                              {event.venue && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  component="span"
                                >
                                  {event.venue}
                                </Typography>
                              )}
                              {(event.assignedCategories.length > 0 ||
                                (event.assignedProjects || []).length > 0) && (
                                <Stack
                                  direction="row"
                                  spacing={0.5}
                                  sx={{ mt: 0.5, flexWrap: 'wrap' }}
                                  component="span"
                                >
                                  {event.assignedCategories.map((c) => (
                                    <Chip
                                      key={c}
                                      size="small"
                                      label={c}
                                      sx={{ height: 20, fontSize: '0.7rem' }}
                                    />
                                  ))}
                                  {(event.assignedProjects || []).map((project) => (
                                    <Chip
                                      key={project.projectId}
                                      size="small"
                                      variant="outlined"
                                      label={project.projectName || project.projectId}
                                      sx={{ height: 20, fontSize: '0.7rem' }}
                                    />
                                  ))}
                                </Stack>
                              )}
                            </Stack>
                          }
                        />
                      </ListItemButton>
                    ))}
                  </List>
                )}
              </Box>
            </Box>

            <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' } }} />

            <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'auto' }}>
              {!focusedEvent ? (
                <Box
                  sx={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px dashed rgba(var(--color-text-rgb), 0.15)',
                    borderRadius: 1,
                    p: 3,
                    textAlign: 'center',
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Select an event on the left to view its details.
                  </Typography>
                </Box>
              ) : (
                <Stack spacing={2}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {focusedEvent.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {formatDateRange(focusedEvent.startDate, focusedEvent.endDate)}
                    </Typography>
                    {focusedEvent.venue && (
                      <Typography variant="body2" color="text.secondary">
                        {focusedEvent.venue}
                      </Typography>
                    )}
                  </Box>

                  {(focusedEvent.assignedCategories.length > 0 ||
                    (focusedEvent.assignedProjects || []).length > 0) && (
                    <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap' }}>
                      {focusedEvent.assignedCategories.map((c) => (
                        <Chip
                          key={c}
                          size="small"
                          label={c}
                          sx={{ height: 22, fontSize: '0.72rem' }}
                        />
                      ))}
                      {(focusedEvent.assignedProjects || []).map((project) => (
                        <Chip
                          key={project.projectId}
                          size="small"
                          variant="outlined"
                          label={project.projectName || project.projectId}
                          sx={{ height: 22, fontSize: '0.72rem' }}
                        />
                      ))}
                    </Stack>
                  )}

                  {focusedEvent.description && (
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                        Description
                      </Typography>
                      <Box
                        sx={{
                          p: 1.5,
                          border: '1px solid rgba(var(--color-text-rgb), 0.12)',
                          borderRadius: 1,
                          bgcolor: 'rgba(var(--color-text-rgb), 0.03)',
                          color: 'var(--color-text)',
                          fontSize: '0.875rem',
                          lineHeight: 1.6,
                          maxHeight: 220,
                          overflow: 'auto',
                          '& img': { maxWidth: '100%', height: 'auto', borderRadius: 1 },
                          '& p': { mt: 0, mb: 1 },
                          '& p:last-child': { mb: 0 },
                        }}
                        dangerouslySetInnerHTML={{
                          __html: normalizeEventDescriptionHtml(focusedEvent.description),
                        }}
                      />
                    </Box>
                  )}

                  <SessionsPanel
                    eventId={focusedEvent.id}
                    eventStartDate={focusedEvent.startDate}
                    eventEndDate={focusedEvent.endDate || focusedEvent.startDate}
                    mode="overview"
                    readOnly
                    onShowQr={(session, qrMode) =>
                      setSessionQrTarget({
                        session,
                        mode: qrMode,
                        eventName: focusedEvent.name,
                      })
                    }
                    onShowAttendees={(session) => setSessionAttendeesTarget(session)}
                  />

                  <Box>
                    {skipsRegistration && (
                      <Alert severity="info" sx={{ mb: 1 }}>
                        This event does not require registration. Attendees can simply turn up
                        and mark their attendance.
                      </Alert>
                    )}
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        mb: 1,
                        borderBottom: 1,
                        borderColor: 'divider',
                      }}
                    >
                      <Tabs
                        value={detailTab}
                        onChange={(_, value) => setDetailTab(value)}
                        variant="scrollable"
                        scrollButtons="auto"
                        allowScrollButtonsMobile
                        sx={{ minHeight: 40, flex: 1, minWidth: 0 }}
                      >
                        {!skipsRegistration && (
                          <Tab
                            value="registered"
                            label={`Registered Users (${registeredMembers.length})`}
                            sx={{
                              textTransform: 'none',
                              fontWeight: 600,
                              minHeight: 40,
                              px: { xs: 1, sm: 2 },
                            }}
                          />
                        )}
                        <Tab
                          value="ecbs"
                          label={`ECB (${ecbInvites.length})`}
                          sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            minHeight: 40,
                            px: { xs: 1, sm: 2 },
                          }}
                        />
                      </Tabs>
                      <Tooltip title="Refresh lists" arrow>
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => focusedEventId && void loadMembers(focusedEventId)}
                            disabled={!focusedEventId || isRefreshing}
                            aria-label="Refresh event members"
                          >
                            {isRefreshing ? (
                              <CircularProgress size={18} />
                            ) : (
                              <RefreshIcon fontSize="small" />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>

                    {!skipsRegistration && detailTab === 'registered' && (
                      registeredMembersError ? (
                        <Alert severity="error">{registeredMembersError}</Alert>
                      ) : registeredMembersLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                          <CircularProgress size={24} />
                        </Box>
                      ) : registeredMembers.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                          No registered users.
                        </Typography>
                      ) : (
                        <Stack spacing={0.75}>
                          {registeredMembers.map((m) => (
                            <Typography key={`${m.ceaNumber}-${m.id}`} variant="body2">
                              {m.name || '—'}
                              {m.ceaNumber ? ` (${m.ceaNumber})` : ''}
                            </Typography>
                          ))}
                        </Stack>
                      )
                    )}

                    {detailTab === 'ecbs' && (
                      ecbInvitesError ? (
                        <Alert severity="error">{ecbInvitesError}</Alert>
                      ) : ecbInvitesLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                          <CircularProgress size={24} />
                        </Box>
                      ) : ecbInvites.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                          No ECBs invited.
                        </Typography>
                      ) : (
                        <Stack spacing={0.75}>
                          {ecbInvites.map((invite, idx) => (
                            <Typography key={`${invite.mobile || ''}-${idx}`} variant="body2">
                              {invite.name}
                              {invite.agency ? ` — ${invite.agency}` : ''}
                              {invite.mobile ? ` (${invite.mobile})` : ''}
                            </Typography>
                          ))}
                        </Stack>
                      )
                    )}
                  </Box>
                </Stack>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid rgba(var(--color-text-rgb), 0.08)' }}>
          <Button onClick={onClose} sx={{ color: 'rgba(var(--color-text-rgb), 0.9)' }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {sessionQrTarget && (
        <EventAttendanceQrDialog
          open={Boolean(sessionQrTarget)}
          onClose={() => setSessionQrTarget(null)}
          eventId={sessionQrTarget.session.eventId}
          eventName={sessionQrTarget.eventName}
          sessionId={sessionQrTarget.session.id}
          sessionName={sessionQrTarget.session.name}
          mode={sessionQrTarget.mode}
          locationRequired={
            events.find((e) => e.id === sessionQrTarget.session.eventId)?.locationRequired === true
          }
        />
      )}

      {sessionAttendeesTarget && (
        <SessionAttendeesDialog
          open={Boolean(sessionAttendeesTarget)}
          onClose={() => setSessionAttendeesTarget(null)}
          sessionId={sessionAttendeesTarget.id}
          sessionName={sessionAttendeesTarget.name}
          sessionDate={sessionAttendeesTarget.sessionDate}
          eventId={sessionAttendeesTarget.eventId}
          eventName={
            events.find((e) => e.id === sessionAttendeesTarget.eventId)?.name || ''
          }
          locationRequired={
            events.find((e) => e.id === sessionAttendeesTarget.eventId)?.locationRequired === true
          }
        />
      )}
    </>
  );
};

export default TrainerManageEventsDialog;
